import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { licenseApi, enumsApi, deviceInstallationApi } from '../services/api';
import { formatProfilePictureUrl } from '../utils/profilePictureHelper';
import {
  DetailPageLayout,
  Card,
  CardHeader,
  CardBody,
  Button,
  FormField,
  FormGroup,
  Badge,
  LoadingSpinner,
  ErrorMessage,
  ConfirmationModal,
  SuccessMessage
} from '../components/common';
import FeatureFlagStats from '../components/FeatureFlagStats';
import { Shield, UserPlus, Smartphone, Trash2 } from 'lucide-react';
import {
  formatLicenseCode,
  getLicenseTypeColor,
  formatDaysUntilExpiration,
  getEnumDisplayName,
  isUserLicenseExpired,
} from '../constants/licenses';
import ModuleBadges from '../components/licenses/ModuleBadges';

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    userName: '',
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    isActive: true
  });
  const [userLicenses, setUserLicenses] = useState([]);
  const [licensesLoading, setLicensesLoading] = useState(true);
  const [licenseTypesRaw, setLicenseTypesRaw] = useState([]);
  const [userDevices, setUserDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [showDeleteAllDevicesModal, setShowDeleteAllDevicesModal] = useState(false);
  const [deleteDeviceModal, setDeleteDeviceModal] = useState({ show: false, id: null });
  const [deleteDevicesLoading, setDeleteDevicesLoading] = useState(false);

  // Fetch license types from API on mount
  useEffect(() => {
    const fetchLicenseTypes = async () => {
      try {
        const response = await enumsApi.getLicenseTypes();
        setLicenseTypesRaw(response.data);
      } catch (err) {
        console.error('Error fetching license types:', err);
        setLicenseTypesRaw([]);
      }
    };
    fetchLicenseTypes();
  }, []);

  useEffect(() => {
    fetchUserDetails();
    fetchUserStatistics();
    fetchUserLicenses();
    fetchUserDevices();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/Users/${userId}`);
      setUser(response.data);
      setFormData({
        userName: response.data.email || '',
        email: response.data.email || '',
        firstName: response.data.firstName || '',
        lastName: response.data.lastName || '',
        phoneNumber: response.data.mobilePhone || '',
        isActive: response.data.isActive !== false
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch user details');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStatistics = async () => {
    try {
      const response = await api.get(`/Users/${userId}/statistics`);
      setStatistics(response.data);
    } catch (err) {
      console.error('Failed to fetch user statistics:', err);
    }
  };

  const fetchUserLicenses = async () => {
    try {
      setLicensesLoading(true);
      const response = await licenseApi.getForUser(userId);
      setUserLicenses(response.data || []);
    } catch (err) {
      console.error('Failed to fetch user licenses:', err);
    } finally {
      setLicensesLoading(false);
    }
  };

  const fetchUserDevices = async () => {
    try {
      setDevicesLoading(true);
      const response = await deviceInstallationApi.getByUser(userId);
      setUserDevices(response.data || []);
    } catch (err) {
      console.error('Failed to fetch user devices:', err);
    } finally {
      setDevicesLoading(false);
    }
  };

  const handleDeleteDevice = async () => {
    try {
      setDeleteDevicesLoading(true);
      await deviceInstallationApi.delete(deleteDeviceModal.id);
      setSuccess('Device deleted successfully');
      fetchUserDevices();
    } catch (err) {
      setError(err.message || 'Failed to delete device');
    } finally {
      setDeleteDevicesLoading(false);
      setDeleteDeviceModal({ show: false, id: null });
    }
  };

  const handleDeleteAllDevices = async () => {
    try {
      setDeleteDevicesLoading(true);
      await deviceInstallationApi.deleteByUser(userId);
      setSuccess('All devices deleted successfully');
      fetchUserDevices();
    } catch (err) {
      setError(err.message || 'Failed to delete devices');
    } finally {
      setDeleteDevicesLoading(false);
      setShowDeleteAllDevicesModal(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUpdateLoading(true);
      setError(null);
      await api.put(`/Users/${userId}`, formData);
      await fetchUserDetails();
      setIsEditing(false);
      setSuccess('User updated successfully');
    } catch (err) {
      setError(err.message || 'Failed to update user');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      setError(null);
      await api.delete(`/Users/${userId}`);
      navigate('/users');
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleStatusToggle = async () => {
    try {
      setStatusLoading(true);
      setError(null);
      await api.put(`/Users/${userId}/status`, { 
        isActive: !user.isActive 
      });
      await fetchUserDetails();
      setSuccess(`User ${user.isActive ? 'deactivated' : 'activated'} successfully`);
    } catch (err) {
      setError(err.message || 'Failed to update user status');
    } finally {
      setStatusLoading(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last || '?';
  };

  const getRoleBadges = () => {
    const roles = [];
    if (user.isAdmin) roles.push({ label: 'Admin', variant: 'danger' });
    if (user.isManager) roles.push({ label: 'Manager', variant: 'primary' });
    if (user.isOwner) roles.push({ label: 'Owner', variant: 'accent' });
    if (user.isResident) roles.push({ label: 'Resident', variant: 'secondary' });
    if (user.isStaff) roles.push({ label: 'Staff', variant: 'outline' });
    return roles;
  };

  if (loading) {
    return <LoadingSpinner size="large" />;
  }

  if (!user) {
    return (
      <DetailPageLayout
        title="User Not Found"
        backLink={{ label: "Back to Users", href: "/users" }}
      >
        <ErrorMessage 
          message="User not found" 
          onDismiss={() => navigate('/users')}
        />
      </DetailPageLayout>
    );
  }

  return (
    <DetailPageLayout
      title={`${user.firstName} ${user.lastName}`}
      subtitle={user.email}
      backLink={{ label: "Back to Users", href: "/users" }}
      actions={!isEditing ? [
        <Button 
          key="edit" 
          variant="primary" 
          onClick={() => setIsEditing(true)}
        >
          Edit
        </Button>,
        <Button 
          key="status" 
          variant={user.isActive !== false ? "danger" : "accent"} 
          onClick={handleStatusToggle}
          loading={statusLoading}
        >
          {user.isActive !== false ? 'Deactivate' : 'Activate'}
        </Button>,
        <Button 
          key="delete" 
          variant="danger" 
          onClick={() => setShowDeleteModal(true)}
        >
          Delete
        </Button>
      ] : []}
    >
      <div className="space-y-6">
        {error && (
          <ErrorMessage 
            message={error} 
            onDismiss={() => setError(null)}
          />
        )}
        
        {success && (
          <SuccessMessage 
            message={success} 
            onDismiss={() => setSuccess(null)}
          />
        )}

        <Card>
          <CardHeader 
            title="User Profile"
            subtitle="View and manage user information"
          />
          <CardBody>
            <div className="flex items-center space-x-6 mb-6">
              {user.profilePictureUrl ? (
                <img
                  src={formatProfilePictureUrl(user.profilePictureUrl)}
                  alt={`${user.firstName} ${user.lastName}`}
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-600 font-semibold text-2xl">
                    {getInitials(user.firstName, user.lastName)}
                  </span>
                </div>
              )}
              <div>
                <h2 className="text-xl font-bold text-dark-900">
                  {user.firstName} {user.lastName}
                </h2>
                <p className="text-gray-600 mb-3">{user.email}</p>
                <div className="flex flex-wrap gap-2">
                  {getRoleBadges().map((role, index) => (
                    <Badge key={index} variant={role.variant}>
                      {role.label}
                    </Badge>
                  ))}
                  <Badge variant={user.isActive !== false ? "accent" : "danger"}>
                    {user.isActive !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </div>

            {isEditing ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <FormGroup title="User Information" description="Edit user details">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      label="Username"
                      name="userName"
                      value={formData.userName}
                      onChange={handleInputChange}
                      required
                    />
                    <FormField
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                    <FormField
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                    <FormField
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                    <FormField
                      label="Phone Number"
                      name="phoneNumber"
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                    />
                  </div>
                </FormGroup>
                <div className="flex justify-end space-x-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={updateLoading}
                  >
                    Save Changes
                  </Button>
                </div>
              </form>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-dark-900">User Information</h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">User ID</dt>
                      <dd className="text-sm text-dark-900">{user.userId}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Phone</dt>
                      <dd className="text-sm text-dark-900">{user.mobilePhone || 'Not provided'}</dd>
                    </div>
                    {user.unitNumber && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Unit</dt>
                        <dd className="text-sm text-dark-900">
                          {user.unitNumber} {user.floor && `- Floor ${user.floor}`}
                        </dd>
                      </div>
                    )}
                    {user.description && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Description</dt>
                        <dd className="text-sm text-dark-900">{user.description}</dd>
                      </div>
                    )}
                  </dl>
                </div>

                {statistics && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-dark-900">Statistics</h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Total Buildings</dt>
                        <dd className="text-sm text-dark-900">{statistics.totalBuildings}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Total Announcements</dt>
                        <dd className="text-sm text-dark-900">{statistics.totalAnnouncements}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Total Deliveries</dt>
                        <dd className="text-sm text-dark-900">{statistics.totalDeliveries}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Created Date</dt>
                        <dd className="text-sm text-dark-900">
                          {new Date(statistics.createdDate).toLocaleDateString()}
                        </dd>
                      </div>
                      {statistics.lastLoginDate && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Last Login</dt>
                          <dd className="text-sm text-dark-900">
                            {new Date(statistics.lastLoginDate).toLocaleString()}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Quick Actions" />
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                variant="secondary"
                onClick={() => navigate(`/users/${userId}/roles`)}
                className="text-center"
              >
                Manage Roles
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate(`/users/${userId}/buildings`)}
                className="text-center"
              >
                View Buildings
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate(`/users/${userId}/reset-password`)}
                className="text-center"
              >
                Reset Password
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={
              <div className="flex items-center">
                <Shield size={20} className="mr-2" />
                Licenses
              </div>
            }
            subtitle="View and manage user license assignments"
            action={
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate('/building-licenses')}
              >
                <UserPlus size={16} className="mr-2" />
                Assign License
              </Button>
            }
          />
          <CardBody>
            {licensesLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : userLicenses.length === 0 ? (
              <div className="text-center py-12">
                <Shield size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-4">This user has no licenses assigned</p>
                <Button
                  variant="primary"
                  onClick={() => navigate('/building-licenses')}
                >
                  <UserPlus size={16} className="mr-2" />
                  Assign First License
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {userLicenses.map((userLicense) => {
                  const activeLicense = userLicense.isActive;
                  return (
                    <div
                      key={userLicense.userLicenseId}
                      className={`border rounded-lg p-4 ${activeLicense ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Link
                              to={`/licenses/${userLicense.licenseId}`}
                              className="font-mono text-lg font-semibold text-primary-600 hover:text-primary-700"
                            >
                              {formatLicenseCode(userLicense.licenseCode)}
                            </Link>
                            <Badge variant={isUserLicenseExpired(userLicense) ? 'danger' : 'success'} size="small">
                              {isUserLicenseExpired(userLicense) ? 'Expired' : 'Active'}
                            </Badge>
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLicenseTypeColor(userLicense.licenseType)}`}>
                            {getEnumDisplayName(userLicense.licenseType, licenseTypesRaw)}
                          </span>
                        </div>
                        {activeLicense && (
                          <Badge variant="accent" className="ml-2">
                            Current
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Activated:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {new Date(userLicense.activatedAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Expires:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {new Date(userLicense.expiresAt).toLocaleDateString()}
                          </span>
                          {activeLicense && (
                            <span className="ml-2 text-xs text-gray-600">
                              ({formatDaysUntilExpiration(userLicense.expiresAt)})
                            </span>
                          )}
                        </div>
                        <div>
                          <span className="text-gray-600">Buildings:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {userLicense.currentBuildingCount} / {userLicense.maxBuildingsAllowed}
                          </span>
                          {userLicense.currentBuildingCount >= userLicense.maxBuildingsAllowed && (
                            <span className="ml-2 text-xs text-red-600 font-medium">Limit reached</span>
                          )}
                        </div>
                        <div>
                          <span className="text-gray-600">Assigned by:</span>
                          <span className="ml-2 text-gray-900">
                            {userLicense.assignedBy || 'Self (code redemption)'}
                          </span>
                        </div>
                      </div>

                      {activeLicense && userLicense.allowedModules && userLicense.allowedModules.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <span className="text-sm text-gray-600 mb-2 block">Available Modules:</span>
                          <ModuleBadges modules={userLicense.allowedModules} variant="badges" maxVisible={6} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={
              <div className="flex items-center">
                <Smartphone size={20} className="mr-2" />
                Registered Devices
              </div>
            }
            subtitle="Push notification device registrations"
            action={
              userDevices.length > 0 && (
                <div className="flex items-center gap-2">
                  <Link to="/device-installations" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                    View All
                  </Link>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setShowDeleteAllDevicesModal(true)}
                  >
                    <Trash2 size={14} className="mr-1" />
                    Delete All
                  </Button>
                </div>
              )
            }
          />
          <CardBody>
            {devicesLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : userDevices.length === 0 ? (
              <div className="text-center py-8">
                <Smartphone size={40} className="mx-auto text-gray-400 mb-3" />
                <p className="text-gray-600">No devices registered</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="pb-2 font-medium">Platform</th>
                      <th className="pb-2 font-medium">Installation ID</th>
                      <th className="pb-2 font-medium">Status</th>
                      <th className="pb-2 font-medium">Last Active</th>
                      <th className="pb-2 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {userDevices.map((device) => (
                      <tr key={device.userDeviceId} className="border-b last:border-0">
                        <td className="py-2">
                          <Badge variant={device.deviceType?.toLowerCase() === 'ios' ? 'primary' : 'accent'}>
                            {device.deviceType?.toUpperCase() || 'Unknown'}
                          </Badge>
                        </td>
                        <td className="py-2">
                          <span className="font-mono text-xs text-gray-600" title={device.installationId}>
                            {device.installationId
                              ? device.installationId.length > 20
                                ? device.installationId.substring(0, 20) + '...'
                                : device.installationId
                              : '—'}
                          </span>
                        </td>
                        <td className="py-2">
                          <Badge variant={device.isActive ? 'accent' : 'danger'}>
                            {device.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="py-2 text-gray-600">
                          {device.lastActiveDate
                            ? new Date(device.lastActiveDate).toLocaleString()
                            : '—'}
                        </td>
                        <td className="py-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteDeviceModal({ show: true, id: device.userDeviceId })}
                          >
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        <FeatureFlagStats userId={userId} />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete User"
        message={`Are you sure you want to delete ${user.firstName} ${user.lastName}? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        loading={deleteLoading}
      />

      <ConfirmationModal
        isOpen={deleteDeviceModal.show}
        onClose={() => setDeleteDeviceModal({ show: false, id: null })}
        onConfirm={handleDeleteDevice}
        title="Delete Device"
        message="Are you sure you want to delete this device? It will be removed from both the database and Azure Notification Hub."
        confirmText="Delete"
        confirmVariant="danger"
        loading={deleteDevicesLoading}
      />

      <ConfirmationModal
        isOpen={showDeleteAllDevicesModal}
        onClose={() => setShowDeleteAllDevicesModal(false)}
        onConfirm={handleDeleteAllDevices}
        title="Delete All Devices"
        message={`Are you sure you want to delete all registered devices for ${user?.firstName} ${user?.lastName}? This will remove them from both the database and Azure Notification Hub.`}
        confirmText="Delete All"
        confirmVariant="danger"
        loading={deleteDevicesLoading}
      />
    </DetailPageLayout>
  );
};

export default UserDetail;